/**
 * Monte modal navigation — Padova style
 *
 * Cuando el modal de imagen del producto se abre, oculta todas las
 * imagenes excepto una a la vez y muestra botones flotantes para
 * navegar entre ellas. Respeta el filtro de color: solo navega
 * sobre las imagenes que el color filter dejo visibles.
 */

(function () {
  const ACTIVE_CLASS = 'monte-modal-active';
  const COLOR_OPTION_NAMES = ['color', 'colour'];

  const normalize = (s) =>
    (s || '')
      .toString()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();

  function getSelectedColor() {
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return null;
    const radios = productInfo.querySelectorAll(
      'variant-radios input[type="radio"]:checked, fieldset input[type="radio"]:checked'
    );
    for (const r of radios) {
      const legend = r.closest('fieldset')?.querySelector('legend')?.textContent || '';
      const optionName = legend.split(':')[0].trim();
      if (COLOR_OPTION_NAMES.includes(normalize(optionName))) return r.value;
    }
    return null;
  }

  function setupModal(modal) {
    if (modal.dataset.monteNavReady === 'true') return;
    const dialog = modal.querySelector('.product-media-modal__dialog');
    const content = modal.querySelector('.product-media-modal__content');
    if (!dialog || !content) return;

    // Estado.
    let currentIndex = 0;

    const allItems = () =>
      Array.from(content.children).filter(
        (el) => el.tagName !== 'BUTTON' && !el.matches('script')
      );

    // Visibles = items cuyo img alt contiene el color seleccionado.
    // Si no hay color seleccionado o no hay alt, se consideran visibles.
    // OJO: en el modal los items pueden ser <img> directamente (no wrappers).
    const getAltFromItem = (el) => {
      if (el.tagName === 'IMG') return el.getAttribute('alt');
      const img = el.querySelector('img');
      if (img) return img.getAttribute('alt');
      return null;
    };

    // Estado del zoom por item (declarado al principio para que showAt lo vea).
    const ZOOM_SCALE = 2;
    const itemState = new WeakMap();

    const applyTransform = (item) => {
      const s = itemState.get(item);
      if (!s) return;
      if (item.classList.contains('monte-zoomed')) {
        item.style.transform = `translate(${s.panX}px, ${s.panY}px) scale(${ZOOM_SCALE})`;
      } else {
        item.style.transform = '';
      }
    };

    const visibleItems = () => {
      const color = getSelectedColor();
      const items = allItems();
      if (!color) {
        items.forEach((it) => {
          it.style.display = '';
          it.removeAttribute('data-monte-hidden');
        });
        return items;
      }
      const colorN = normalize(color);
      const visible = [];
      items.forEach((it) => {
        const alt = normalize(getAltFromItem(it));
        if (!alt || alt.includes(colorN)) {
          it.style.display = '';
          it.removeAttribute('data-monte-hidden');
          visible.push(it);
        } else {
          it.style.display = 'none';
          it.setAttribute('data-monte-hidden', 'true');
        }
      });
      return visible;
    };

    const showAt = (idx) => {
      const visible = visibleItems();
      if (!visible.length) return;
      currentIndex = ((idx % visible.length) + visible.length) % visible.length;
      allItems().forEach((it) => {
        it.classList.remove(ACTIVE_CLASS);
        it.classList.remove('monte-zoomed');
        it.classList.remove('monte-grabbing');
        it.style.transform = '';
        it.style.transformOrigin = '';
        const s = itemState && itemState.get && itemState.get(it);
        if (s) { s.panX = 0; s.panY = 0; s.dragging = false; s.didDrag = false; }
      });
      visible[currentIndex].classList.add(ACTIVE_CLASS);
      visible[currentIndex].scrollIntoView?.({ behavior: 'instant', block: 'center' });
    };

    // Crear botones de navegacion.
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'monte-modal-prev';
    prev.setAttribute('aria-label', 'Anterior');
    prev.innerHTML = '<span aria-hidden="true">&lsaquo;</span>';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'monte-modal-next';
    next.setAttribute('aria-label', 'Siguiente');
    next.innerHTML = '<span aria-hidden="true">&rsaquo;</span>';

    // Dawn cierra el media-modal en pointerup; detenemos la propagacion en
    // pointerdown/pointerup/mouseup para que no llegue al modal,
    // pero SIN stopImmediatePropagation (eso bloquearia el click handler).
    const stopBubble = (e) => {
      e.stopPropagation();
    };
    ['pointerdown', 'pointerup', 'mousedown', 'mouseup'].forEach((evt) => {
      prev.addEventListener(evt, stopBubble);
      next.addEventListener(evt, stopBubble);
    });
    prev.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      showAt(currentIndex - 1);
    });
    next.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      showAt(currentIndex + 1);
    });

    dialog.appendChild(prev);
    dialog.appendChild(next);

    // Zoom + pan al hacer click/drag en la imagen activa.
    const setupImageZoom = () => {
      allItems().forEach((item) => {
        if (item.dataset.monteZoomReady === 'true') return;
        item.dataset.monteZoomReady = 'true';
        itemState.set(item, { panX: 0, panY: 0, dragging: false, didDrag: false });

        // Detener pointer events para que Dawn no cierre el modal.
        // OJO: NO bloquear 'mouseup' aqui, porque rompe el fin del drag.
        ['pointerdown', 'pointerup'].forEach((evt) => {
          item.addEventListener(evt, (e) => e.stopPropagation());
        });

        // Drag para panear cuando esta zoomed.
        let startX = 0, startY = 0, startPanX = 0, startPanY = 0;

        item.addEventListener('mousedown', (e) => {
          e.stopPropagation();
          if (!item.classList.contains('monte-zoomed')) return;
          const s = itemState.get(item);
          e.preventDefault();
          s.dragging = true;
          s.didDrag = false;
          startX = e.clientX;
          startY = e.clientY;
          startPanX = s.panX;
          startPanY = s.panY;
          item.classList.add('monte-grabbing');
        });

        const onMove = (e) => {
          const s = itemState.get(item);
          if (!s || !s.dragging) return;
          const dx = e.clientX - startX;
          const dy = e.clientY - startY;
          if (Math.abs(dx) > 3 || Math.abs(dy) > 3) s.didDrag = true;
          s.panX = startPanX + dx;
          s.panY = startPanY + dy;
          applyTransform(item);
        };

        const onUp = () => {
          const s = itemState.get(item);
          if (!s || !s.dragging) return;
          s.dragging = false;
          item.classList.remove('monte-grabbing');
        };

        // Usar captura para que mouseup llegue siempre, incluso si algun
        // descendiente hace stopPropagation. Tambien escuchamos mouseleave
        // a nivel de window por si el cursor sale del documento.
        document.addEventListener('mousemove', onMove, true);
        document.addEventListener('mouseup', onUp, true);
        window.addEventListener('blur', onUp);
        document.addEventListener('mouseleave', onUp);

        item.addEventListener('click', (e) => {
          if (!item.classList.contains(ACTIVE_CLASS)) return;
          const s = itemState.get(item);
          e.preventDefault();
          e.stopPropagation();
          // Si hubo drag real, no togglear zoom.
          if (s.didDrag) {
            s.didDrag = false;
            return;
          }
          if (item.classList.contains('monte-zoomed')) {
            // Zoom out: limpiar pan.
            item.classList.remove('monte-zoomed');
            s.panX = 0;
            s.panY = 0;
            applyTransform(item);
          } else {
            // Zoom in: centrar el zoom donde se clickeo.
            const rect = item.getBoundingClientRect();
            const cx = rect.width / 2;
            const cy = rect.height / 2;
            const offsetX = (e.clientX - rect.left) - cx;
            const offsetY = (e.clientY - rect.top) - cy;
            s.panX = -offsetX;
            s.panY = -offsetY;
            item.classList.add('monte-zoomed');
            applyTransform(item);
          }
        });
      });
    };

    // Inicializar mostrando la primera visible.
    const init = () => {
      // Si hay una imagen marcada como activa por Dawn, usarla.
      const dawnActive = allItems().find((el) => el.classList.contains('active'));
      const visible = visibleItems();
      if (dawnActive && visible.includes(dawnActive)) {
        currentIndex = visible.indexOf(dawnActive);
      } else {
        currentIndex = 0;
      }
      // Quitar zoom de todos antes de cambiar.
      allItems().forEach((it) => it.classList.remove('monte-zoomed'));
      showAt(currentIndex);
      setupImageZoom();
    };

    // Navegacion con teclado.
    document.addEventListener('keydown', (e) => {
      if (!modal.hasAttribute('open')) return;
      if (e.key === 'ArrowLeft') showAt(currentIndex - 1);
      if (e.key === 'ArrowRight') showAt(currentIndex + 1);
    });

    // Re-inicializar cuando se abre el modal o cambia el filtro.
    new MutationObserver(() => {
      if (modal.hasAttribute('open')) init();
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });

    modal.addEventListener('monte:items-filtered', () => {
      if (modal.hasAttribute('open')) init();
    });

    if (modal.hasAttribute('open')) init();
    else init();

    modal.dataset.monteNavReady = 'true';
  }

  function start() {
    document.querySelectorAll('product-modal').forEach(setupModal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
